// src/pages/PaymentCallback.js
//
// This page is what Paystack redirects the user back to after checkout.
// It verifies the payment, resolves the selected plan and market, and
// lets the seller finish registration with market-specific categories.

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getApiErrorMessage, getStoredToken } from "../config/api";
import { marketService } from "../services/marketService";
import { paymentService } from "../services/paymentService";
import { planService } from "../services/planService";
import { profilerService } from "../services/profilerService";
import {
  getDuplicateRequestResolution,
  hasActiveSuccessfulPlanPayment,
  resolveMarketplaceAccessState,
} from "../utils/marketplaceAccess";
import {
  clearPendingPlanSelection,
  getPendingPlanSelection,
  persistPendingSellerRequest,
} from "../utils/sellerOnboarding";
import {
  ArrowLeft,
  CheckCircle,
  Loader2,
  Tag,
  Trash2,
  XCircle,
} from "lucide-react";

const STATUS = {
  VERIFYING: "verifying",
  LOADING: "loading",
  READY: "ready",
  SUBMITTING: "submitting",
  PENDING: "pending",
  FAILED: "failed",
  NO_REFERENCE: "no_reference",
  NO_PLAN: "no_plan",
  NO_MARKET: "no_market",
};

const trimFormData = (formData) => ({
  shopName: formData.shopName.trim(),
  storeAddress: formData.storeAddress.trim(),
  note: formData.note.trim(),
});

const normalizeStatus = (value) => String(value || "").trim().toLowerCase();

const isSuccessfulVerification = (verificationResult) => {
  const responseData = verificationResult?.data || {};
  const status = normalizeStatus(
    responseData?.status || responseData?.paymentStatus || responseData?.data?.status,
  );

  return Boolean(verificationResult?.ok) && (
    responseData?.verified === true ||
    status === "success" ||
    status === "successful"
  );
};

const hasExplicitVerificationFailure = (verificationResult) => {
  const responseData = verificationResult?.data || {};
  const status = normalizeStatus(
    responseData?.status || responseData?.paymentStatus || responseData?.data?.status,
  );

  return (
    responseData?.verified === false ||
    status === "failed" ||
    status === "failure" ||
    status === "abandoned" ||
    status === "cancelled" ||
    status === "canceled"
  );
};

type SellerRequestPayload = {
  profilerId: string;
  marketId: string;
  planId: string;
  shopName: string;
  storeAddress: string;
  note?: string;
  nicheCategoryIds: string[];
};

const normalizeMarketCategories = (responseData, fallbackMarket) => {
  if (Array.isArray(responseData)) {
    return {
      market: fallbackMarket,
      categories: responseData,
    };
  }

  if (Array.isArray(responseData?.categories)) {
    return {
      market: responseData?.market || fallbackMarket,
      categories: responseData.categories,
    };
  }

  if (Array.isArray(fallbackMarket?.categories)) {
    return {
      market: fallbackMarket,
      categories: fallbackMarket.categories,
    };
  }

  return {
    market: responseData?.market || fallbackMarket,
    categories: [],
  };
};

export default function PaymentCallback() {
  const {
    user,
    loading: authLoading,
    refreshProfile,
    setMarketplaceAccess,
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingPlanSelection = useMemo(() => getPendingPlanSelection(), []);
  const isTenantOnboardingFlow = Boolean(
    user?.hasTenantAccess && pendingPlanSelection.marketId,
  );
  const postPaymentPath = isTenantOnboardingFlow
    ? "/tenant"
    : "/dashboard/pricing";
  const pricingReturnPath = "/dashboard/pricing";

  const [status, setStatus] = useState(STATUS.VERIFYING);
  const [message, setMessage] = useState("Verifying your payment...");
  const [plan, setPlan] = useState(null);
  const [market, setMarket] = useState(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [formData, setFormData] = useState<{
    shopName: string;
    storeAddress: string;
    note: string;
  }>({
    shopName: "",
    storeAddress: "",
    note: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  const reference = useMemo(
    () =>
      searchParams.get("reference") ||
      searchParams.get("trxref") ||
      pendingPlanSelection.paymentReference,
    [pendingPlanSelection.paymentReference, searchParams],
  );

  const storedPlanId = useMemo(
    () =>
      searchParams.get("planId") ||
      pendingPlanSelection.planId ||
      user?.planId,
    [pendingPlanSelection.planId, searchParams, user?.planId],
  );

  const authToken = useMemo(
    () =>
      user?.token ||
      getStoredToken() ||
      sessionStorage.getItem("profiler_token"),
    [user?.token],
  );

  const profilerId = useMemo(
    () => user?.id || localStorage.getItem("profiler_id") || null,
    [user?.id],
  );

  const verificationQuery = useQuery({
    queryKey: ["payment-verification", reference],
    queryFn: async () => paymentService.verifyPaymentResult(reference, authToken),
    enabled: !authLoading && Boolean(reference),
  });

  const verificationResult = verificationQuery.data;
  const verificationData = verificationResult?.data;
  const verificationSucceeded = isSuccessfulVerification(verificationResult);
  const verificationFailed = hasExplicitVerificationFailure(verificationResult);

  const registrationDataQuery = useQuery({
    queryKey: ["payment-callback-registration", storedPlanId, profilerId, authToken],
    enabled:
      verificationQuery.isSuccess &&
      verificationSucceeded &&
      Boolean(authToken) &&
      Boolean(profilerId),
    queryFn: async () => {
      const [profileResult, activePlanResult] = await Promise.all([
        profilerService.getMe(authToken).catch(() => null),
        profilerId ? planService.getActivePlanPayment(profilerId).catch(() => null) : null,
      ]);

      const accessState = resolveMarketplaceAccessState({
        user,
        profile: profileResult,
        activePlanPayment: activePlanResult,
      });

      if (profileResult?.seller || accessState.status === "approved") {
        return {
          kind: "approved",
          plan:
            profileResult?.seller?.plan ||
            accessState.activePlan ||
            activePlanResult?.plan ||
            null,
        };
      }

      if (!hasActiveSuccessfulPlanPayment(activePlanResult)) {
        throw new Error(
          "Your payment was verified, but the active plan payment is not marked successful yet.",
        );
      }

      const effectivePlanId =
        activePlanResult?.planId || activePlanResult?.plan?.id || storedPlanId;

      const [planResult, marketsResult] = await Promise.all([
        effectivePlanId
          ? planService.getPlanById(effectivePlanId).catch(() => activePlanResult?.plan || null)
          : Promise.resolve(activePlanResult?.plan || null),
        marketService.listMarkets(),
      ]);

      const markets = Array.isArray(marketsResult) ? marketsResult : [];

      if (!markets.length) {
        throw new Error("Could not load available markets.");
      }

      const request = activePlanResult?.request || null;
      const requestStatus = normalizeStatus(request?.status);

      const marketId =
        request?.marketId ||
        request?.market?.id ||
        pendingPlanSelection.marketId ||
        planResult?.markets?.[0]?.id ||
        accessState.activePlan?.markets?.[0]?.id ||
        null;

      const marketCandidate =
        markets.find((item) => item.id === marketId) ||
        request?.market ||
        planResult?.markets?.[0] ||
        activePlanResult?.plan?.markets?.find((item) => item.id === marketId) ||
        null;

      if (request && requestStatus !== "rejected") {
        return {
          kind: "pending",
          plan: planResult || activePlanResult?.plan || accessState.activePlan || null,
          market: marketCandidate,
        };
      }

      if (!effectivePlanId || !planResult?.id) {
        return { kind: "no_plan" };
      }

      if (!marketId || !marketCandidate) {
        return { kind: "no_market" };
      }

      const categoriesResult = await marketService.getMarketCategories(marketId);
      const normalizedCategories = normalizeMarketCategories(
        categoriesResult,
        marketCandidate,
      );

      return {
        kind: "ready",
        plan: planResult,
        market: normalizedCategories.market || marketCandidate,
        categories: normalizedCategories.categories,
      };
    },
  });

  const submitRequestMutation = useMutation({
    mutationFn: async (payload: SellerRequestPayload) =>
      marketService.createSellerRequest(payload),
  });

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!reference) {
      setStatus(STATUS.NO_REFERENCE);
      setMessage(
        "No payment reference found. Please return to payment or contact support.",
      );
      return;
    }

    if (!authToken) {
      setStatus(STATUS.FAILED);
      setMessage("Authentication required. Please log in again.");
      return;
    }

    if (!profilerId) {
      setStatus(STATUS.FAILED);
      setMessage("Profiler account could not be resolved. Please log in again.");
      return;
    }

    if (verificationQuery.isLoading) {
      setStatus(STATUS.VERIFYING);
      setMessage("Verifying your payment...");
      return;
    }

    if (verificationQuery.isError) {
      setStatus(STATUS.FAILED);
      setMessage(
        getApiErrorMessage(verificationQuery.error, "Payment verification failed."),
      );
      return;
    }

    if (verificationFailed) {
      setStatus(STATUS.FAILED);
      setMessage(
        verificationData?.message ||
          "Payment could not be confirmed. Please contact support.",
      );
      return;
    }

    if (verificationQuery.isSuccess && !verificationSucceeded) {
      setStatus(STATUS.FAILED);
      setMessage(
        verificationData?.message ||
          "Payment verification did not return a successful state.",
      );
      return;
    }

    if (verificationQuery.isSuccess) {
      setStatus(STATUS.LOADING);
      setMessage("Payment confirmed. Loading your registration data...");
    }
  }, [
    authLoading,
    authToken,
    profilerId,
    reference,
    verificationData?.message,
    verificationQuery.error,
    verificationQuery.isError,
    verificationQuery.isLoading,
    verificationQuery.isSuccess,
    verificationSucceeded,
    verificationFailed,
  ]);

  useEffect(() => {
    if (status !== STATUS.LOADING || !authToken) {
      return;
    }

    setSubmitError("");
    setErrors({});

    if (registrationDataQuery.isLoading) {
      return;
    }

    if (registrationDataQuery.isError) {
      setStatus(STATUS.FAILED);
      setMessage(
        getApiErrorMessage(
          registrationDataQuery.error,
          "Failed to load registration data.",
        ),
      );
      return;
    }

    const registrationData = registrationDataQuery.data;

    if (!registrationData) {
      return;
    }

    if (registrationData.kind === "approved") {
      clearPendingPlanSelection();
      setMarketplaceAccess(registrationData.plan);
      void refreshProfile();
      navigate(postPaymentPath, { replace: true });
      return;
    }

    if (registrationData.kind === "pending") {
      clearPendingPlanSelection();
      persistPendingSellerRequest({
        profilerId,
        planId: registrationData.plan?.id || storedPlanId,
        marketId: registrationData.market?.id || pendingPlanSelection.marketId || "",
        submittedAt: new Date().toISOString(),
      });
      setPlan(registrationData.plan);
      setMarket(registrationData.market);
      setAlreadySubmitted(true);
      setStatus(STATUS.PENDING);
      setMessage(
        "Payment confirmed. Your seller request is recorded and seller tools will unlock after provisioning.",
      );
      return;
    }

    if (registrationData.kind === "no_plan") {
      setStatus(STATUS.NO_PLAN);
      setMessage(
        "Could not resolve your selected plan. Please return to pricing and try again.",
      );
      return;
    }

    if (registrationData.kind === "no_market") {
      setStatus(STATUS.NO_MARKET);
      setMessage(
        "Could not determine the market linked to this plan. Please contact support.",
      );
      return;
    }

    setPlan(registrationData.plan);
    setMarket(registrationData.market);
    setCategories(registrationData.categories || []);
    setStatus(STATUS.READY);
    setMessage("Complete your seller setup.");
  }, [
    authToken,
    navigate,
    pendingPlanSelection.marketId,
    postPaymentPath,
    profilerId,
    refreshProfile,
    registrationDataQuery.data,
    registrationDataQuery.error,
    registrationDataQuery.isError,
    registrationDataQuery.isLoading,
    setMarketplaceAccess,
    status,
    storedPlanId,
  ]);

  useEffect(() => {
    if (status !== STATUS.PENDING || !authToken) {
      return;
    }

    let isActive = true;

    const pollSellerProvisioning = async () => {
      try {
        const profile = await profilerService.getMe(authToken);

        if (!isActive || !profile?.seller) {
          return;
        }

        clearPendingPlanSelection();
        await refreshProfile();
        navigate(postPaymentPath, { replace: true });
      } catch {
        // Keep polling quietly until the seller account is provisioned.
      }
    };

    pollSellerProvisioning();
    const intervalId = window.setInterval(pollSellerProvisioning, 8000);

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
    };
  }, [authToken, navigate, postPaymentPath, refreshProfile, status]);

  const isLoadingData = registrationDataQuery.isLoading;

  const maxCategories = Number(plan?.maxCategories ?? 0);
  const categoryLimitReached =
    maxCategories > 0 && selectedCategoryIds.length >= maxCategories;

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategoryIds((prev) => {
      const alreadySelected = prev.includes(categoryId);

      if (alreadySelected) {
        return prev.filter((id) => id !== categoryId);
      }

      if (categoryLimitReached) {
        return prev;
      }

      return [...prev, categoryId];
    });
  };

  const removeSelectedCategory = (categoryId) => {
    setSelectedCategoryIds((prev) => prev.filter((id) => id !== categoryId));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (alreadySubmitted) return;
    setSubmitError("");

    const trimmed = trimFormData(formData);
    const newErrors:Record<string, any> = {};

    if (!trimmed.shopName) {
      newErrors.shopName = "Shop name is required.";
    }

    if (!trimmed.storeAddress) {
      newErrors.storeAddress = "Store address is required.";
    }

    if (!selectedCategoryIds.length) {
      newErrors.nicheCategoryIds = "Select at least one category.";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    if (!plan?.id || !market?.id || !profilerId) {
      setSubmitError(
        "Unable to resolve profiler, plan, or market. Please reload the page.",
      );
      return;
    }

    if (!authToken) {
      setSubmitError("Authentication expired. Please log in again.");
      return;
    }

    const requestBody = {
      profilerId,
      marketId: market.id,
      planId: plan.id,
      shopName: trimmed.shopName,
      storeAddress: trimmed.storeAddress,
      note: trimmed.note,
      nicheCategoryIds: selectedCategoryIds,
    };

    setStatus(STATUS.SUBMITTING);

    try {
      const responseData = await submitRequestMutation.mutateAsync(requestBody);

      if (!responseData) {
        const duplicateRequestState = getDuplicateRequestResolution(
          responseData?.message || responseData?.error,
        );

        if (duplicateRequestState === "approved") {
          clearPendingPlanSelection();
          setMarketplaceAccess(plan);
          await refreshProfile();
          navigate(postPaymentPath, { replace: true });
          return;
        }

        if (duplicateRequestState === "pending") {
          clearPendingPlanSelection();
          persistPendingSellerRequest({
            profilerId,
            planId: plan.id,
            marketId: market.id,
            submittedAt: new Date().toISOString(),
          });
          setAlreadySubmitted(true);
          setResult(responseData);
          setStatus(STATUS.PENDING);
          setMessage(
            "Payment confirmed. Your seller request is recorded and seller tools will unlock after provisioning.",
          );
          return;
        }

        throw new Error(
          responseData?.message ||
            responseData?.error ||
            "Could not submit seller registration.",
        );
      }

      const createdRequest = responseData?.data || responseData;

      persistPendingSellerRequest({
        id: createdRequest?.id || null,
        profilerId,
        marketId: market.id,
        planId: plan.id,
        submittedAt: new Date().toISOString(),
      });

      setAlreadySubmitted(true);
      setResult(createdRequest);
      setStatus(STATUS.PENDING);
      setMessage(
        "Your seller request has been submitted. Seller tools will unlock after provisioning.",
      );
      clearPendingPlanSelection();
    } catch (err) {
      const errorData = err?.response?.data || {};
      const duplicateRequestState = getDuplicateRequestResolution(
        errorData?.message || errorData?.error,
      );

      if (duplicateRequestState === "approved") {
        clearPendingPlanSelection();
        setMarketplaceAccess(plan);
        await refreshProfile();
        navigate(postPaymentPath, { replace: true });
        return;
      }

      if (duplicateRequestState === "pending") {
        clearPendingPlanSelection();
        persistPendingSellerRequest({
          profilerId,
          planId: plan.id,
          marketId: market.id,
          submittedAt: new Date().toISOString(),
        });
        setAlreadySubmitted(true);
        setResult(errorData);
        setStatus(STATUS.PENDING);
        setMessage(
          "Payment confirmed. Your seller request is recorded and seller tools will unlock after provisioning.",
        );
        return;
      }

      setStatus(STATUS.READY);
      setSubmitError(getApiErrorMessage(err, "Could not submit registration."));
    }
  };

  const renderHeader = () => {
    if (status === STATUS.PENDING) return "Pending Admin Approval";
    if (status === STATUS.READY) return "Complete Seller Setup";
    if (status === STATUS.LOADING) return "Loading registration data";
    if (status === STATUS.VERIFYING) return "Verifying payment";
    return "Seller Onboarding";
  };

  const stepLabel =
    status === STATUS.READY || status === STATUS.SUBMITTING
      ? "Final step"
      : status === STATUS.PENDING
        ? "Awaiting review"
        : "Step 1 of 2";

  const progressPercent =
    maxCategories > 0
      ? Math.min((selectedCategoryIds.length / maxCategories) * 100, 100)
      : 0;

  const progressColor =
    selectedCategoryIds.length >= maxCategories
      ? "bg-rose-500"
      : selectedCategoryIds.length / Math.max(maxCategories, 1) >= 0.6
        ? "bg-amber-400"
        : "bg-violet-500";

  const dropdownNote =
    maxCategories === 999
      ? `Select any categories from ${market?.name || "your market"}.`
      : `${selectedCategoryIds.length} of ${maxCategories} categories selected.`;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-10 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-violet-500 font-semibold">
              {stepLabel}
            </p>
            <h1 className="text-3xl font-black text-gray-900 dark:text-gray-100 mt-2">
              {renderHeader()}
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-2xl">
              {message}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/tenant")}
            className="inline-flex items-center gap-2 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition">
            <ArrowLeft size={16} /> Return to properties
          </button>
        </div>

        {status === STATUS.VERIFYING && (
          <div className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-10 text-center">
            <Loader2
              size={44}
              className="mx-auto mb-4 text-violet-500 animate-spin"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Verifying your payment reference and resolving selected plan
              details.
            </p>
          </div>
        )}

        {status === STATUS.LOADING && (
          <div className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-10 text-center">
            <Loader2
              size={44}
              className="mx-auto mb-4 text-violet-500 animate-spin"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Fetching plans and marketplace categories for the plan you
              selected.
            </p>
          </div>
        )}

        {(status === STATUS.FAILED ||
          status === STATUS.NO_REFERENCE ||
          status === STATUS.NO_PLAN ||
          status === STATUS.NO_MARKET) && (
          <div className="rounded-3xl border border-rose-200 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/50 p-6">
            <div className="flex items-start gap-3">
              <XCircle size={28} className="text-rose-500" />
              <div>
                <h2 className="text-lg font-semibold text-rose-900 dark:text-rose-100">
                  Something went wrong
                </h2>
                <p className="mt-2 text-sm text-rose-800 dark:text-rose-300">
                  {message}
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(pricingReturnPath)}
                    className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-500 transition">
                    Review pricing
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(postPaymentPath)}
                    className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 transition">
                    {postPaymentPath === "/tenant" ? "Back to properties" : "Back to pricing"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {status === STATUS.READY && !isLoadingData && plan && market && (
          <div className="grid gap-6 lg:grid-cols-[1.3fr_0.9fr]">
            <form
              onSubmit={handleSubmit}
              className="space-y-6 rounded-3xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 p-6">
              <div className="space-y-4">
                <div className="rounded-3xl border border-gray-100 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-800">
                  <p className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400 font-semibold">
                    Plan & market
                  </p>
                  <div className="mt-3 flex flex-wrap gap-3 text-sm text-gray-700 dark:text-gray-300">
                    <span className="rounded-2xl bg-violet-100 px-3 py-1.5 text-violet-700 dark:bg-violet-500/10 dark:text-violet-200">
                      {plan.name}
                    </span>
                    <span className="rounded-2xl bg-slate-100 px-3 py-1.5 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                      {market.name}, {market.city || "Unknown city"}
                    </span>
                    <span className="rounded-2xl bg-slate-100 px-3 py-1.5 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                      max categories:{" "}
                      {maxCategories === 999 ? "All" : maxCategories}
                    </span>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Shop Name
                    <input
                      name="shopName"
                      value={formData.shopName}
                      onChange={handleInputChange}
                      className="mt-2 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                      placeholder="Enter your shop name"
                    />
                    {errors.shopName && (
                      <p className="mt-2 text-xs text-rose-500">
                        {errors.shopName}
                      </p>
                    )}
                  </label>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Store Address
                    <input
                      name="storeAddress"
                      value={formData.storeAddress}
                      onChange={handleInputChange}
                      className="mt-2 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                      placeholder="Enter your store address"
                    />
                    {errors.storeAddress && (
                      <p className="mt-2 text-xs text-rose-500">
                        {errors.storeAddress}
                      </p>
                    )}
                  </label>
                </div>

                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Note (optional)
                  <textarea
                    name="note"
                    value={formData.note}
                    onChange={handleInputChange}
                    rows={4}
                    className="mt-2 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    placeholder="Add an optional note for review"
                  />
                </label>
              </div>

              <div className="space-y-3 rounded-3xl border border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Niche category selection
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {dropdownNote}
                    </p>
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-[0.25em] text-gray-400">
                    {selectedCategoryIds.length}/
                    {maxCategories === 999 ? categories.length : maxCategories}
                  </div>
                </div>

                <div className="grid gap-2 max-h-64 overflow-y-auto rounded-3xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                  {categories.length ? (
                    categories.map((category) => {
                      const selected = selectedCategoryIds.includes(
                        category.id,
                      );
                      const disabled = !selected && categoryLimitReached;
                      return (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() =>
                            !disabled && toggleCategory(category.id)
                          }
                          className={`flex items-center justify-between gap-4 rounded-2xl border px-4 py-3 text-left transition ${
                            selected
                              ? "border-violet-500 bg-violet-50 text-violet-800 dark:border-violet-400/60 dark:bg-violet-500/10 dark:text-violet-200"
                              : disabled
                                ? "border-gray-200 bg-gray-100 text-gray-400 opacity-70 cursor-not-allowed dark:border-gray-700 dark:bg-gray-900"
                                : "border-gray-200 bg-white text-gray-700 hover:border-violet-300 hover:bg-violet-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                          }`}>
                          <span className="text-sm font-medium">
                            {category.name}
                          </span>
                          <span className="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                            {selected
                              ? "Selected"
                              : disabled
                                ? "Locked"
                                : "Add"}
                          </span>
                        </button>
                      );
                    })
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No categories were returned for this market.
                    </p>
                  )}
                </div>

                {errors.nicheCategoryIds && (
                  <p className="text-xs text-rose-500">
                    {errors.nicheCategoryIds}
                  </p>
                )}

                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {selectedCategoryIds.map((categoryId) => {
                      const category = categories.find(
                        (item) => item.id === categoryId,
                      );
                      return (
                        <span
                          key={categoryId}
                          className="inline-flex items-center gap-2 rounded-full bg-violet-100 px-3 py-1 text-sm text-violet-700 dark:bg-violet-500/10 dark:text-violet-200">
                          <Tag size={14} />
                          {category?.name || categoryId}
                          <button
                            type="button"
                            onClick={() => removeSelectedCategory(categoryId)}
                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-violet-500 text-white transition hover:bg-violet-400">
                            <Trash2 size={12} />
                          </button>
                        </span>
                      );
                    })}
                  </div>

                  <div className="rounded-2xl overflow-hidden bg-gray-200 dark:bg-gray-800">
                    <div
                      className={`h-2 ${progressColor}`}
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {submitError && (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-700 dark:bg-rose-950/40 dark:text-rose-200">
                    {submitError}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={status === STATUS.SUBMITTING || alreadySubmitted}
                  className="w-full rounded-2xl bg-violet-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-60">
                  {status === STATUS.SUBMITTING
                    ? "Submitting..."
                    : alreadySubmitted
                      ? "Submitted"
                      : "Submit registration"}
                </button>
              </div>
            </form>

            <div className="space-y-4 rounded-3xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Plan summary
                </h2>
                <div className="mt-3 text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <div className="flex items-center justify-between">
                    <span>Plan name</span>
                    <span className="font-semibold">{plan.name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Market</span>
                    <span className="font-semibold">{market.name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Max categories</span>
                    <span className="font-semibold">
                      {maxCategories === 999 ? "Unlimited" : maxCategories}
                    </span>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-950">
                <p className="text-xs uppercase tracking-[0.25em] text-gray-500 dark:text-gray-400 font-semibold">
                  Selection status
                </p>
                <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                  {selectedCategoryIds.length === 0
                    ? "Choose at least one niche category to continue."
                    : `${selectedCategoryIds.length} niche ${selectedCategoryIds.length === 1 ? "category" : "categories"} selected.`}
                </p>
              </div>
            </div>
          </div>
        )}

        {status === STATUS.PENDING && (
          <div className="rounded-3xl border border-amber-200 bg-amber-50 p-8 dark:border-amber-700/40 dark:bg-amber-950/20">
            <div className="flex items-center gap-3">
              <CheckCircle size={32} className="text-amber-500" />
              <div>
                <h2 className="text-xl font-bold text-amber-900 dark:text-amber-100">
                  Pending Admin Approval
                </h2>
                <p className="mt-1 text-sm text-amber-700 dark:text-amber-200">
                  {message}
                </p>
              </div>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-3xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
                <p className="text-xs uppercase tracking-[0.25em] text-gray-500 dark:text-gray-400 font-semibold">
                  Shop
                </p>
                <p className="mt-2 font-semibold text-gray-900 dark:text-gray-100">
                  {formData.shopName || user?.shopName || "Seller account"}
                </p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {formData.storeAddress || user?.storeAddress || "Address pending"}
                </p>
              </div>
              <div className="rounded-3xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
                <p className="text-xs uppercase tracking-[0.25em] text-gray-500 dark:text-gray-400 font-semibold">
                  Plan & market
                </p>
                <p className="mt-2 font-semibold text-gray-900 dark:text-gray-100">
                  {plan?.name || "Selected plan"}
                </p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {market?.name || "Assigned market"}
                </p>
              </div>
            </div>

            {result?.id && (
              <div className="mt-6 rounded-3xl border border-amber-200 bg-white p-5 dark:border-amber-700/40 dark:bg-amber-950/20">
                <p className="text-xs uppercase tracking-[0.25em] text-gray-500 dark:text-gray-400 font-semibold">
                  Request ID
                </p>
                <p className="mt-2 font-mono text-sm text-gray-700 dark:text-gray-100">
                  {result.id}
                </p>
              </div>
            )}

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => navigate("/tenant")}
                className="flex-1 rounded-2xl bg-violet-600 px-5 py-3 text-sm font-semibold text-white hover:bg-violet-500 transition">
                Back to My Properties
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
